import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { checkUsageLimit, UsageLimitExceededError, incrementUsage } from "@/hooks/useUsageLimits";
import { getContentSourceText } from "@/lib/content/getContentSourceText";
import type { ContentType } from "@/types/content";
import { resolveModel } from "@/lib/ai/models";

const SYSTEM_PROMPT = `You are Shattara AI, a friendly, focused study tutor for high school and university students in Saudi Arabia and the MENA region.

Core behavior:
- Explain concepts clearly, step-by-step, using simple language first, then add deeper detail if needed.
- Encourage active learning: ask short follow-up questions, propose practice questions, and suggest ways to check understanding.
- Adapt depth and tone to the student's level based on how they ask the question.
- When a question is ambiguous, briefly ask a clarifying question instead of guessing.
- When the student is stuck, break the problem into small steps and guide them through.
- Prioritize correctness over creativity. If you are unsure, say so and suggest how the student can verify.

Content handling:
- If you are given specific text, notes, a PDF, or transcript, use that as the primary source when answering.
- If there is a conflict between your general knowledge and the provided material, explain both and say which is which.
- For math or science derivations, always show key steps, not just the final answer.

Safety and integrity:
- Do not write full graded assignments or essays for the student. Instead, help them outline, brainstorm, and improve their own work.
- Do not invent citations, references, or fake sources.
- Avoid harmful, hateful, or adult content and redirect to safe, educational topics.

Formatting:
- Use short paragraphs and bullets when it helps readability.
- Use inline LaTeX-style math (like x^2 + y^2) only when it's helpful, not everywhere.
- For definitions or procedures, you can use numbered steps (1., 2., 3., …).

Always keep your goal in mind:
Help the student understand, not just memorize; make them feel more confident about studying.

Study tools:
When the user mentions @Mind Map, respond with a mindmap code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the mind map.
IMPORTANT constraints:
- Maximum 4-5 top-level nodes (branches from root). Never exceed 5.
- Maximum 2-3 children per node. Prioritize the most important points.
- Keep labels short (1-3 words).

\`\`\`mindmap
{
  "title": "Topic Title",
  "root": "Central Concept",
  "nodes": [
    { "id": "1", "label": "Branch 1", "children": [
      { "id": "1a", "label": "Sub-point A" },
      { "id": "1b", "label": "Sub-point B" }
    ]},
    { "id": "2", "label": "Branch 2" }
  ],
  "description": "Brief summary of the mind map."
}
\`\`\`

When the user mentions @Timeline, respond with a timeline code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the timeline.
IMPORTANT constraints:
- Maximum 8-10 items. Prioritize the most significant events.
- Keep titles short (2-5 words).
- Dates should be concise (e.g. "1889", "1914-1918", "June 1944").

\`\`\`timeline
{
  "title": "Timeline Title",
  "items": [
    { "id": "1", "date": "1900", "title": "Event Title", "description": "Brief description" },
    { "id": "2", "date": "1950", "title": "Another Event", "description": "What happened" }
  ]
}
\`\`\`

When the user mentions @Flowchart, respond with a flowchart code block containing structured JSON. Do NOT include any other text before the code block. After the code block, add a brief paragraph explaining the flowchart.
IMPORTANT constraints:
- Maximum 8-12 nodes. Prioritize key steps and decision points.
- Use "rect" shape for events/actions, "diamond" for yes/no decisions.
- Keep labels short (1-3 words per line, max 3 lines).
- Use colors meaningfully: blue for start, green for positive, purple for actions, orange for transitions, yellow for outcomes.
- Edges connect nodes by id. Add "label" only for decision branches (e.g. "Yes", "No").

\`\`\`flowchart
{
  "title": "Process Title",
  "nodes": [
    { "id": "A", "label": ["Step One", "Detail"], "shape": "rect", "color": "blue" },
    { "id": "B", "label": ["Decision?"], "shape": "diamond", "color": "green" },
    { "id": "C", "label": ["Result"], "shape": "rect", "color": "purple" }
  ],
  "edges": [
    { "from": "A", "to": "B" },
    { "from": "B", "to": "C", "label": "Yes" }
  ]
}
\`\`\``;

export function useChatSession(chatId: string | undefined) {
  const [messages, setMessages] = useState<any[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [hasTriggeredAI, setHasTriggeredAI] = useState(false);
  const [initialSearchEnabled, setInitialSearchEnabled] = useState(false);
  const [contentSourceText, setContentSourceText] = useState<string | null>(null);
  const [contentRefsReady, setContentRefsReady] = useState(false);
  const { toast } = useToast();

  // Fetch source text for content references
  const fetchContentSourceTexts = useCallback(async (contentIds: string[]) => {
    try {
      // Get content types for each ID
      const { data: contents, error } = await supabase
        .from("content")
        .select("id, type, title")
        .in("id", contentIds);

      if (error || !contents) return;

      const texts: string[] = [];
      let totalLength = 0;
      const MAX_CHARS = 50000;

      for (const content of contents) {
        if (totalLength >= MAX_CHARS) break;
        const text = await getContentSourceText(content.id, content.type as ContentType);
        if (text) {
          const remaining = MAX_CHARS - totalLength;
          texts.push(`--- Document: "${content.title}" (${content.type}) ---\n${text.slice(0, remaining)}`);
          totalLength += text.length;
        }
      }

      if (texts.length > 0) {
        setContentSourceText(texts.join("\n\n"));
      }
    } catch (err) {
      console.error("Error fetching content source texts:", err);
    }
  }, []);

  // Load chat history
  const loadHistory = useCallback(async () => {
    if (!chatId) return;

    const { data, error } = await supabase
      .from("chat_messages")
      .select("*")
      .eq("content_id", chatId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error loading chat history:", error);
      return;
    }

    if (data) {
      setMessages(data);

      // Read metadata from first user message for search/content references
      const firstUserMsg = data.find((m: any) => m.role === 'user');
      if (firstUserMsg?.metadata) {
        const meta = firstUserMsg.metadata as any;
        if (meta.searchEnabled) {
          setInitialSearchEnabled(true);
        }
        if (meta.contentReferenceIds && Array.isArray(meta.contentReferenceIds) && meta.contentReferenceIds.length > 0) {
          await fetchContentSourceTexts(meta.contentReferenceIds);
          setContentRefsReady(true);
        } else {
          setContentRefsReady(true);
        }
      } else {
        setContentRefsReady(true);
      }
    }
  }, [chatId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Auto-trigger AI if last message is from user (for dashboard-created chats)
  // Wait for contentRefsReady so referenced content text is available in the system prompt
  useEffect(() => {
    if (!chatId || !messages.length || hasTriggeredAI || isStreaming || !contentRefsReady) return;
    
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.role === 'user') {
      // Trigger AI response automatically
      setHasTriggeredAI(true);
      triggerAIResponse();
    }
  }, [messages, chatId, hasTriggeredAI, isStreaming, contentRefsReady]);

  const triggerAIResponse = useCallback(async () => {
    if (!chatId) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    setIsStreaming(true);

    try {
      // Build full history for LLM
      const systemContent = contentSourceText
        ? `${SYSTEM_PROMPT}\n\n--- Referenced Content ---\n${contentSourceText}`
        : SYSTEM_PROMPT;
      const history = [
        { role: "system", content: systemContent },
        ...messages.map((m) => ({
          role: m.role,
          content: m.message,
        })),
      ];

      // Prepare assistant message placeholder
      let assistantText = "";
      const collectedAnnotations: any[] = [];
      const streamingId = "streaming-" + Date.now();
      
      setMessages((prev) => [
        ...prev,
        { id: streamingId, role: "assistant", message: "", created_at: new Date().toISOString() },
      ]);

      // Stream AI tokens
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) throw new Error("No active session");
      
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/chat-stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ messages: history, searchEnabled: initialSearchEnabled }),
      });

      if (!resp.ok) {
        throw new Error(`HTTP error! status: ${resp.status}`);
      }

      const reader = resp.body!.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });
        
        // Process line-by-line as data arrives
        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") continue;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              assistantText += content;
              
              // Update streaming message
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === streamingId
                    ? { ...m, message: assistantText }
                    : m
                )
              );
            }
            // Capture annotations (typically arrive at end of stream)
            const annotations = parsed.choices?.[0]?.delta?.annotations;
            if (annotations && Array.isArray(annotations)) {
              collectedAnnotations.push(...annotations);
            }
          } catch {
            // Incomplete JSON split across chunks: put it back and wait for more data
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      // Replace streaming placeholder with real DB row
      const { data: aiMessage, error: aiError } = await supabase
        .from("chat_messages")
        .insert({
          content_id: chatId,
          user_id: user.id,
          role: "assistant",
          message: assistantText,
          metadata: collectedAnnotations.length > 0 ? { annotations: collectedAnnotations } : null,
        })
        .select()
        .single();

      if (aiError) throw aiError;

      setMessages((prev) =>
        prev.map((m) => (m.id === streamingId ? aiMessage : m))
      );
    } catch (error) {
      console.error("Error getting AI response:", error);
      toast({
        title: "Error",
        description: "Failed to get AI response. Please try again.",
        variant: "destructive",
      });
      
      // Remove streaming placeholder on error
      setMessages((prev) => prev.filter((m) => !m.id?.toString().startsWith("streaming-")));
    } finally {
      setIsStreaming(false);
    }
  }, [chatId, messages, toast, contentSourceText, initialSearchEnabled]);

  // Send new message (user → AI)
  const sendMessage = useCallback(
    async (userText: string, options?: { searchEnabled?: boolean; model?: string }) => {
      if (!chatId) {
        toast({
          title: "Error",
          description: "Chat ID is missing",
          variant: "destructive",
        });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to send messages",
          variant: "destructive",
        });
        return;
      }

      // Check usage limit before sending
      const limitCheck = await checkUsageLimit("chat_messages");
      if (!limitCheck.canProceed) {
        throw new UsageLimitExceededError("chat_messages", limitCheck.used, limitCheck.limit);
      }

      setIsStreaming(true);

      try {
        // Increment usage for chat message
        await incrementUsage("chat_messages");

        // Insert user message
        const { data: userMessage, error: userError } = await supabase
          .from("chat_messages")
          .insert({
            content_id: chatId,
            user_id: user.id,
            role: "user",
            message: userText,
          })
          .select()
          .single();

        if (userError) throw userError;

        setMessages((prev) => [...prev, userMessage]);

        // Build full history for LLM
        const systemContent = contentSourceText
          ? `${SYSTEM_PROMPT}\n\n--- Referenced Content ---\n${contentSourceText}`
          : SYSTEM_PROMPT;
        const history = [
          { role: "system", content: systemContent },
          ...messages.map((m) => ({
            role: m.role,
            content: m.message,
          })),
          { role: "user", content: userText },
        ];

        // Prepare assistant message placeholder
        let assistantText = "";
        const collectedAnnotations: any[] = [];
        const streamingId = "streaming-" + Date.now();
        
        setMessages((prev) => [
          ...prev,
          { id: streamingId, role: "assistant", message: "", created_at: new Date().toISOString() },
        ]);

        // Stream AI tokens
        const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
        const { data: { session } } = await supabase.auth.getSession();
        const accessToken = session?.access_token;
        if (!accessToken) throw new Error("No active session");
        
        const resp = await fetch(`${SUPABASE_URL}/functions/v1/chat-stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            messages: history,
            searchEnabled: options?.searchEnabled || false,
            model: resolveModel(options?.model || "auto"),
          }),
        });

        if (!resp.ok) {
          throw new Error(`HTTP error! status: ${resp.status}`);
        }

        const reader = resp.body!.getReader();
        const decoder = new TextDecoder();
        let textBuffer = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          textBuffer += decoder.decode(value, { stream: true });
          
          // Process line-by-line as data arrives
          let newlineIndex: number;
          while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
            let line = textBuffer.slice(0, newlineIndex);
            textBuffer = textBuffer.slice(newlineIndex + 1);

            if (line.endsWith("\r")) line = line.slice(0, -1);
            if (line.startsWith(":") || line.trim() === "") continue;
            if (!line.startsWith("data: ")) continue;

            const jsonStr = line.slice(6).trim();
            if (jsonStr === "[DONE]") continue;

            try {
              const parsed = JSON.parse(jsonStr);
              const content = parsed.choices?.[0]?.delta?.content as string | undefined;
              if (content) {
                assistantText += content;
                
                // Update streaming message
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === streamingId
                      ? { ...m, message: assistantText }
                      : m
                  )
                );
              }
              // Capture annotations (typically arrive at end of stream)
              const annotations = parsed.choices?.[0]?.delta?.annotations;
              if (annotations && Array.isArray(annotations)) {
                collectedAnnotations.push(...annotations);
              }
            } catch {
              // Incomplete JSON split across chunks: put it back and wait for more data
              textBuffer = line + "\n" + textBuffer;
              break;
            }
          }
        }

        // Replace streaming placeholder with real DB row
        const { data: aiMessage, error: aiError } = await supabase
          .from("chat_messages")
          .insert({
            content_id: chatId,
            user_id: user.id,
            role: "assistant",
            message: assistantText,
            metadata: collectedAnnotations.length > 0 ? { annotations: collectedAnnotations } : null,
          })
          .select()
          .single();

        if (aiError) throw aiError;

        setMessages((prev) =>
          prev.map((m) => (m.id === streamingId ? aiMessage : m))
        );
      } catch (error) {
        console.error("Error sending message:", error);
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive",
        });
        
        // Remove streaming placeholder on error
        setMessages((prev) => prev.filter((m) => !m.id?.toString().startsWith("streaming-")));
      } finally {
        setIsStreaming(false);
      }
    },
    [chatId, messages, toast, contentSourceText]
  );

  return {
    messages,
    isStreaming,
    initialSearchEnabled,
    sendMessage,
    reload: loadHistory,
  };
}
